/*
 * Restock notification — one global controller for the one modal
 * (snippets/restock-modal.liquid, rendered by layout/theme.liquid).
 *
 * Plain ES5-ish JS on purpose: the theme's other assets are pre-built webpack
 * bundles, but this ships as a standalone file so it needs no build step and
 * runs on the same browsers the rest of the theme supports. No jQuery;
 * textContent only.
 *
 * Triggers are found by document-level click delegation on
 * [data-restock-trigger], so buttons inside quick-view clones and AJAX-rendered
 * product cards work without re-binding. Two trigger shapes:
 *   · PDP: data-variants (JSON map) + data-select-id — the SKU follows the
 *     variant dropdown, because the backend joins on SKU and a shopper waiting
 *     on "Near Mint" must not be emailed when "Lightly Played" comes back.
 *   · cards / wishlist: explicit data-sku, data-variant-id, data-variant-title,
 *     data-image-url, data-price-cents.
 * window.PkmRestock.open({...}) opens it programmatically (wishlist page).
 *
 * Identity: the request carries NO email and no identity field. The App Proxy
 * signs the customer id; the admin app reads the account's address from
 * Shopify when it sends. Signed out (or no email on the account) the modal
 * renders no form, so there is nothing to submit.
 */
(function () {
  'use strict';

  if (window.PkmRestock) return;

  var root = null;
  var modal = null;
  var form = null;
  var feedback = null;
  var submit = null;
  var productLine = null;
  var current = null;
  var openedAt = 0;
  var lastFocused = null;
  var closeTimer = null;

  function bind() {
    if (root) return true;
    root = document.querySelector('[data-restock-modal-root]');
    if (!root) return false;
    modal = root.querySelector('[data-restock-modal]');
    form = root.querySelector('[data-restock-form]');
    feedback = root.querySelector('[data-restock-feedback]');
    submit = root.querySelector('[data-restock-submit]');
    productLine = root.querySelector('[data-restock-product]');
    if (form) form.addEventListener('submit', onSubmit);
    return true;
  }

  function say(message, kind) {
    if (!feedback) return;
    feedback.textContent = message || '';
    feedback.className = 'restock-modal__feedback' +
      (kind ? ' restock-modal__feedback--' + kind : '');
  }

  function toInt(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? null : n;
  }

  function variantFromMap(trigger) {
    var variants = [];
    try {
      variants = JSON.parse(trigger.getAttribute('data-variants') || '[]');
    } catch (e) {
      variants = [];
    }
    // The hidden #ProductSelect drives every other piece of theme JS, so it is
    // the single source of truth for "which variant is on screen".
    var select = trigger.getAttribute('data-select-id')
      ? document.getElementById(trigger.getAttribute('data-select-id'))
      : null;
    var id = select && select.value ? parseInt(select.value, 10) : NaN;
    var i;
    for (i = 0; i < variants.length; i++) {
      if (variants[i].id === id) return variants[i];
    }
    // Fall back to the first variant lacking stock — that is the one the
    // shopper is looking at when the notify button renders at all.
    for (i = 0; i < variants.length; i++) {
      if (!variants[i].available) return variants[i];
    }
    return variants[0] || null;
  }

  function dataFromTrigger(trigger) {
    var d = trigger.dataset;
    var base = {
      productHandle: d.productHandle || null,
      productTitle: d.productTitle || null
    };
    if (trigger.hasAttribute('data-variants')) {
      var v = variantFromMap(trigger);
      base.sku = v ? v.sku : null;
      base.variantId = v ? v.id : null;
      base.variantTitle = v ? v.title : null;
      base.imageUrl = v ? v.image : null;
      base.priceCents = v ? v.price : null;
      return base;
    }
    base.sku = d.sku || null;
    base.variantId = toInt(d.variantId);
    base.variantTitle = d.variantTitle || null;
    base.imageUrl = d.imageUrl || null;
    base.priceCents = toInt(d.priceCents);
    return base;
  }

  // Signed out, the modal's sign-in links return to this page. Liquid can only
  // render request.path, and the PDP variant picker doesn't touch the URL, so
  // pin the variant the shopper asked about or they come back to the default.
  function pointLoginLinks(variantId) {
    var loc = window.location;
    var back = loc.pathname + loc.search;
    if (variantId && loc.pathname.indexOf('/products/') !== -1) {
      back = loc.pathname + '?variant=' + variantId;
    }
    var links = modal.querySelectorAll('[data-login-template]');
    for (var i = 0; i < links.length; i++) {
      links[i].href = links[i].getAttribute('data-login-template')
        .replace('__RETURN__', encodeURIComponent(back));
    }
  }

  function open(data) {
    if (!bind() || !modal) return;
    window.clearTimeout(closeTimer);
    current = data || {};
    lastFocused = document.activeElement;

    if (productLine) {
      var variantTitle = current.variantTitle && current.variantTitle !== 'Default Title'
        ? ' — ' + current.variantTitle
        : '';
      productLine.textContent = (current.productTitle || '') + variantTitle;
    }
    pointLoginLinks(current.variantId);
    if (form) form.reset();
    if (submit) {
      submit.hidden = false;
      submit.disabled = false;
    }
    say('', '');

    modal.hidden = false;
    document.body.classList.add('restock-modal-open');
    openedAt = Date.now();
    // Defer so the element is laid out before focus, or iOS Safari scrolls
    // the page instead of the field.
    window.setTimeout(function () {
      var target = (form && form.querySelector('input[name="desired_quantity"]')) ||
        modal.querySelector('[data-restock-login]');
      if (target && target.focus) target.focus();
    }, 30);
  }

  function close() {
    if (!modal) return;
    window.clearTimeout(closeTimer);
    modal.hidden = true;
    document.body.classList.remove('restock-modal-open');
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function onSubmit(event) {
    event.preventDefault();
    var data = current || {};

    if (!data.sku) {
      // No SKU means the backend has nothing to match on. Say so rather than
      // accepting a subscription that could never fire.
      say(root.dataset.msgError, 'error');
      return;
    }

    var qtyField = form.querySelector('input[name="desired_quantity"]');
    var qty = toInt(qtyField && qtyField.value);
    var marketing = form.querySelector('input[name="marketing_opt_in"]');
    var honeypot = form.querySelector('input[name="hp"]');

    submit.disabled = true;
    say('', '');

    fetch(root.dataset.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        sku: data.sku,
        variant_id: data.variantId,
        variant_title: data.variantTitle,
        product_handle: data.productHandle,
        product_title: data.productTitle,
        image_url: data.imageUrl,
        price_cents: data.priceCents,
        desired_quantity: qty !== null && qty >= 1 && qty <= 999 ? qty : null,
        marketing_opt_in: !!(marketing && marketing.checked),
        locale: root.dataset.locale,
        hp: honeypot ? honeypot.value : '',
        elapsed_ms: Date.now() - openedAt
      })
    })
      .then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (body) {
          return { status: response.status, body: body || {} };
        });
      })
      .then(function (result) {
        if (result.status >= 200 && result.status < 300 && result.body.ok !== false) {
          say(root.dataset.msgSuccess, 'success');
          submit.hidden = true;
          closeTimer = window.setTimeout(close, 2600);
          document.dispatchEvent(new CustomEvent('pkm:restock-subscribed', {
            detail: { sku: data.sku, code: result.body.code || null }
          }));
          return;
        }
        say(root.dataset.msgError, 'error');
        submit.disabled = false;
      })
      .catch(function (error) {
        if (window.console && console.warn) console.warn('restock subscribe failed', error);
        say(root.dataset.msgError, 'error');
        submit.disabled = false;
      });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var trigger = target.closest('[data-restock-trigger]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      open(dataFromTrigger(trigger));
      return;
    }
    if (modal && !modal.hidden && target.closest('[data-restock-close]')) {
      event.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && modal && !modal.hidden) close();
  });

  window.PkmRestock = { open: open, close: close };

  // The wishlist page used to link a sold-out product here with ?notify=1, and
  // old links still do: land on the form rather than making the shopper find
  // the button. Only the PDP trigger (the one carrying the variant map) opens.
  function autoOpen() {
    bind();
    if (!/[?&]notify=1(&|$)/.test(window.location.search)) return;
    var trigger = document.querySelector('[data-restock-trigger][data-variants]');
    if (!trigger) return;
    trigger.scrollIntoView({ block: 'center' });
    open(dataFromTrigger(trigger));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoOpen);
  } else {
    autoOpen();
  }
})();
